import React, { useContext, useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import notify from '~/components/Notify';
import { NewsletterQuery } from '~/QueryHooks/NewsLetterQuery';
import { El } from '~/components/Layout/style';
import { Link } from 'react-router-dom';
import { RouteTypes } from '~/routes/constants';
import { ThemeContext } from 'styled-components';
import i18n from '~/components/i18n';
import { Input, Popup } from 'semantic-ui-react';
import { useAuth } from '~/routes/components/AuthProvider';
import dayjs from 'dayjs';
import CustomIcon from '~/components/CustomIcon';
import NewsletterService from '~/api/services/newsletter';
import { useDebounce } from 'use-lodash-debounce';
import TableComponent from '~/components/TableComponent';
import Dialog from '~/components/Dialog';
import { LIGHT_USER_ROLE_SLUG } from '~/Constants';
import { formatNumberWithoutFractions, handleErrors } from '~/utils/helpers';
import { DownloadService } from '~/api/services/downloadFiles';

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Created at'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Type'),
    key: 'type',
    type: 'newsletter-type',
    sorted: 'desc'
  },
  {
    name: i18n.t('Subject'),
    key: 'subject'
  },
  {
    name: i18n.t('Status'),
    key: 'sent',
    type: 'newsletter',
    sorted: 'desc'
  },
  {
    name: i18n.t('Send date'),
    key: 'sendDate',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Newsletter status'),
    key: 'totalDelivered',
    type: 'percent-status-news'
  },
  {
    name: i18n.t('Recipients'),
    key: 'totalRecipients'
  },
  {
    name: i18n.t('Opened via web/mobile app'),
    key: 'totalOpenedInApp',
    type: 'percent'
  },
  {
    name: i18n.t('Actions')
  }
]

const Newsletter: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { systemData: { logged: { roleSlug } } } = useAuth();
  const { firstColor } = useContext(ThemeContext);

  const [isBusy, setIsBusy] = useState(false)
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [deleteNewsletterId, setDeleteNewsletterId] = useState(null)
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    search: ''
  })
  const [search, setSearch] = useState('');
  const debouncedSearchValue = useDebounce(search, 300);
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG

  const {
    isLoading: isBusyData,
    data: newsletterData,
    isFetching: isFetching,
    refetch: fetchFilteredData
  } = NewsletterQuery.getNewsLetterList(
    {
      ...dataFiltered,
      page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
    });

  const { filtered, rows } = newsletterData ? newsletterData : { filtered: 0, rows: [] }

  useEffect(() => {
    fetchFilteredData()
    setTableColumnHeaders(getDefaultTableColumnHeaders())
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData()
    }
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  async function handleNewsletterSummary(newsletter, type) {
    const { id } = newsletter
    setLoadingAction({
      value: true,
      type,
      id: id,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFileInAnotherWindow(`newsletter/${id}/summary/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      value: false,
      type: '',
      id: -1,
      idKey: ''
    })
  }

  const getActions = (newsletter, actionType) => {
    const { sendDate, isDraft } = newsletter;
    const now = dayjs(new Date())
    const endDate = dayjs(sendDate);
    const displayEditAction = isDraft ? true : (now.diff(endDate) < 0);
    const displayResultsAction = (now.diff(endDate) > 0);
    switch (actionType) {
      case 'edit':
        return displayEditAction ? <Popup
          content={t('Edit')}
          trigger={
            <Link to={RouteTypes.NEWSLETTER_EDIT.replace(':id', newsletter.id)}>
              <CustomIcon style={{ cursor: 'pointer' }} name='pencil' size='large' />
            </Link>
          }
        /> : null
      case 'delete':
        return displayEditAction && <Popup
          content={t('Delete')}
          trigger={<CustomIcon
            onClick={() => {
              setDeleteNewsletterId(newsletter.id)
            }}
            name='trash'
            color='red'
            size='large'
          />}
        />
      case 'result':
        return displayResultsAction ? <Popup
          content={t('Results')}
          trigger={<CustomIcon
            onClick={() => handleNewsletterSummary(newsletter, actionType)}
            name='clipboard outline'
            size='large'
          />}
        /> : null
      default:
        return
    }
  }

  function renderNewsLetterType(value) {
    switch (value) {
      case 'email':
        return <Popup
          content={t('Standard email')}
          trigger={<CustomIcon name='envelope' size='large' />}
        />
      case 'intern':
        return <Popup
          content={t('Newsletter intern')}
          trigger={<CustomIcon name='envelope open' size='large' />}
        />
      case 'full':
        return <Popup
          content={t('Newsletter with E-mail')}
          trigger={<CustomIcon className='icon-newsletter-2' size='large' />}
        />
      default:
        return
    }
  }

  function renderStatusNewsletter(data) {
    const { totalSent, totalDelivered, totalOpened, totalRecipients } = data
    return <div>
      <div>{t('Sent: {{sent}} ({{setPercent}} %)',
        {
          sent: totalSent,
          setPercent: totalRecipients > 0 ? formatNumberWithoutFractions(totalSent / totalRecipients * 100) : 0
        }
      )}</div>
      <div>{t('Delivered: {{delivered}} ({{deliverPercent}} %)', {
        delivered: totalDelivered,
        deliverPercent: totalRecipients > 0 ? formatNumberWithoutFractions(totalDelivered / totalRecipients * 100) : 0
      })}</div>
      <div>{t('Opened: {{opened}} ({{openPercent}} %)', {
        opened: totalOpened,
        openPercent: totalRecipients > 0 ? formatNumberWithoutFractions(totalOpened / totalRecipients * 100) : 0
      })}</div>
    </div>
  }

  function getNewsletterStatusIcon(row, value) {
    if(row.isDraft) {
      return <Popup
        content={t('Draft')}
        trigger={<CustomIcon className='icon-file-milk' />}
      />
    } else if (row.isDraft === 0 && row.schedule === 1) {
      return <Popup
        content={t('Planned')}
        trigger={<CustomIcon name='clock' />}
      />
    } else if (row.sent === 1) {
      return <Popup
        content={t('Sent')}
        trigger={<CustomIcon className='icon-newsletter-1' />}
      />
    } else {
      return <Popup
        content={t('Sending')}
        trigger={<CustomIcon className='envelope' />}
      />
    }
  }

  function renderColumn(type, value, row) {
    switch (type) {
      case 'percent-status-news':
        return renderStatusNewsletter(row)
      case 'newsletter-type':
        return renderNewsLetterType(value)
      case 'newsletter':
        return getNewsletterStatusIcon(row, value)
      case 'date':
        return dayjs(value).format('LLLL')
      default:
        return value
    }
  }

  const renderContent = () => {
    const actions = [
      {
        popupMessage: i18n.t('Show more'),
        type: 'link',
        name: 'eye',
        size: 'large',
        link: RouteTypes.NEWSLETTER_SHOW,
        lightUserAction: true
      },
      {
        popupMessage: i18n.t('Edit'),
        type: 'link',
        name: 'pencil',
        size: 'large',
        link: RouteTypes.NEWSLETTER_EDIT,
        renderActions: getActions,
        actionType: 'edit'
      },
      {
        popupMessage: i18n.t('Delete'),
        type: 'icon',
        name: 'trash',
        size: 'large',
        renderActions: getActions,
        actionType: 'delete'
      },
      {
        popupMessage: i18n.t('Results'),
        type: 'icon',
        name: 'clipboard outline',
        size: 'large',
        renderActions: getActions,
        actionType: 'result',
        lightUserAction: true
      }
    ]
    return <TableComponent
      tableColumnHeaders={tableColumnHeaders}
      rows={rows || []}
      animated
      total={filtered}
      actions={lightUserLogged ? actions.filter(action => !!action.lightUserAction) : actions}
      isFetchingData={isFetching}
      dataFiltered={dataFiltered}
      loading={isBusy}
      loadingAction={loadingAction}
      renderColumn={(type, value, row) => renderColumn(type, value, row)}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
    />
  };

  const renderActions = () => {
    return !lightUserLogged && <>
      <El.PageHeaderActionButton
        as={Link}
        to={RouteTypes.NEWSLETTER_CREATE}
        style={{ backgroundColor: firstColor }}
      >{t('Create newsletter')}</El.PageHeaderActionButton>
    </>;
  };

  const deleteNewsletter = async (id: string) => {
    setIsBusy(true);
    try {
      await NewsletterService.deleteNewsletter(id);
      notify(t('Newsletter deleted'), 'success');
      setDeleteNewsletterId(null);
      fetchFilteredData();
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setIsBusy(false);
  };

  function renderSearch() {
    return (
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    );
  }

  return <Layout
    isBusy={isBusy || isBusyData}
    title={t('Newsletter')}
    segmented
    filters={renderSearch()}
    actions={renderActions()}>
    {renderContent()}
    <Dialog
      isDialogOpen={!!deleteNewsletterId}
      textClose={t('No')}
      type={'abort'}
      textContent={t('Are you sure you want to delete this newsletter')}
      textHeader={t('Delete newsletter')}
      handleClose={() => setDeleteNewsletterId(null)}
      handleSubmit={() => deleteNewsletter(deleteNewsletterId)}
      textSubmit={t('Yes')}
    />
  </Layout>;
};

export default Newsletter;
